import { useEffect, useState } from 'react';

/**
 * What playback resolves for a library video: the stream URL plus the
 * user-facing title and description an admin wrote for it.
 */
export interface VideoPlaybackInfo {
  url: string;
  title?: string;
  description?: string;
}

/**
 * Resolves a library video's playback info once and caches it for the life
 * of the module — every card showing the same video (e.g. an admin's own
 * card while editing, and that same video reused elsewhere) shares one
 * fetch instead of each re-resolving it. `getPlaybackUrl` is injected
 * rather than imported directly so this one hook works from both the admin
 * bundle (adminBackendServiceVideoLibraryMixin.getVideoPlaybackUrl) and the
 * investor bundle (BaseBackendServiceFundDomain.getVideoPlaybackUrl) —
 * same backend contract, two separate BackendService classes.
 */
const infoCache = new Map<string, VideoPlaybackInfo>();
const inFlight = new Map<string, Promise<VideoPlaybackInfo>>();

/** Drops a video's cached info so the next card to show it re-fetches (e.g. after its metadata was edited). */
export function invalidatePlaybackInfo(externalId: string): void {
  infoCache.delete(externalId);
  inFlight.delete(externalId);
}

export interface UsePlaybackUrlResult {
  url: string | null;
  title: string | undefined;
  description: string | undefined;
  loading: boolean;
  error: string | null;
}

export function usePlaybackUrl(
  externalId: string | undefined,
  getPlaybackUrl: (externalId: string) => Promise<VideoPlaybackInfo>,
): UsePlaybackUrlResult {
  const [info, setInfo] = useState<VideoPlaybackInfo | null>(
    externalId ? (infoCache.get(externalId) ?? null) : null,
  );
  const [loading, setLoading] = useState(!!externalId && !info);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!externalId) {
      setInfo(null);
      setLoading(false);
      return;
    }
    const cached = infoCache.get(externalId);
    if (cached) {
      setInfo(cached);
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    let request = inFlight.get(externalId);
    if (!request) {
      request = getPlaybackUrl(externalId).finally(() => inFlight.delete(externalId));
      inFlight.set(externalId, request);
    }

    request
      .then((resolved) => {
        infoCache.set(externalId, resolved);
        if (!cancelled) {
          setInfo(resolved);
          setLoading(false);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err?.message ?? 'Unable to load video');
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [externalId]);

  return {
    url: info?.url ?? null,
    title: info?.title,
    description: info?.description,
    loading,
    error,
  };
}
